import React, { useState } from 'react';
import {
  LineChart, Line, XAxis, YAxis, Tooltip, Legend,
  CartesianGrid, ResponsiveContainer,
} from 'recharts';
import ChartFrame from './ChartFrame';

export interface InvestmentProjectionOption {
  key: string;
  label: string;
  annual_rate_pct: number;
  rate_source: 'live' | 'fallback' | 'static';
  final_value: number;
}

export interface InvestmentProjectionData {
  type: 'investment_projection';
  amount: number;
  monthly_contribution: number;
  years: number;
  as_of: string;
  options: InvestmentProjectionOption[];
  series: Array<Record<string, number>>; // { year, [optionKey]: value }
  disclaimer: string;
}

interface Props {
  data: InvestmentProjectionData;
}

// Fixed categorical order, one color per option key — validated against the
// chat's dark chart surface (CVD ΔE 22.3); the purple's low contrast is
// relieved by the legend and the Data table view.
const LINE_COLORS = ['#667eea', '#764ba2', '#2a9d8f', '#dd6b20'];

const formatCurrency = (value: number) =>
  Math.abs(value) >= 1_000_000
    ? `$${(value / 1_000_000).toFixed(1)}M`
    : Math.abs(value) >= 1000
      ? `$${(value / 1000).toFixed(1)}k`
      : `$${value.toFixed(0)}`;

const formatFull = (value: number) =>
  `$${value.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

const InvestmentProjectionChart: React.FC<Props> = ({ data }) => {
  const [showRaw, setShowRaw] = useState(false);

  if (!data.series?.length || !data.options?.length) {
    return (
      <div className="chart-wrapper">
        <p className="chart-empty">No projection data available.</p>
      </div>
    );
  }

  const title = `Growth of $${data.amount.toLocaleString('en-US')}${
    data.monthly_contribution > 0
      ? ` + $${data.monthly_contribution.toLocaleString('en-US')}/mo`
      : ''
  } over ${data.years} years`;

  const legendLabel = (option: InvestmentProjectionOption) =>
    `${option.label} · ${option.annual_rate_pct}%${option.rate_source === 'fallback' ? ' est.' : ''}`;

  const renderChart = (fullscreen: boolean) => (
    <ResponsiveContainer width="100%" height={fullscreen ? '100%' : 300}>
      <LineChart data={data.series} margin={{ top: 8, right: 16, bottom: 8, left: 16 }}>
        <CartesianGrid strokeDasharray="3 3" stroke="#efefef" />
        <XAxis
          dataKey="year"
          tickFormatter={(year: number) => `Yr ${year}`}
          tick={{ fontSize: 11 }}
          interval="preserveStartEnd"
        />
        <YAxis
          tickFormatter={formatCurrency}
          tick={{ fontSize: 11 }}
          width={58}
        />
        <Tooltip
          formatter={(value: number, name: string) => [formatFull(value), name]}
          labelFormatter={(year) => `Year ${year}`}
        />
        <Legend wrapperStyle={{ fontSize: 11 }} />
        {data.options.map((option, i) => (
          <Line
            key={option.key}
            type="monotone"
            dataKey={option.key}
            name={legendLabel(option)}
            stroke={LINE_COLORS[i % LINE_COLORS.length]}
            strokeWidth={2.5}
            dot={false}
            activeDot={{ r: 5 }}
          />
        ))}
      </LineChart>
    </ResponsiveContainer>
  );

  const renderTable = () => (
    <table className="chart-data-table">
      <thead>
        <tr>
          <th>Year</th>
          {data.options.map((option) => (
            <th key={option.key}>{option.label}</th>
          ))}
        </tr>
      </thead>
      <tbody>
        {data.series.map((row) => (
          <tr key={row.year}>
            <td>{row.year}</td>
            {data.options.map((option) => (
              <td key={option.key}>{formatFull(row[option.key] ?? 0)}</td>
            ))}
          </tr>
        ))}
      </tbody>
    </table>
  );

  return (
    <>
      <ChartFrame
        title={title}
        showRaw={showRaw}
        onToggle={setShowRaw}
        renderChart={renderChart}
        renderTable={renderTable}
      />
      <p className="chart-disclaimer">
        Rates as of {data.as_of}. {data.disclaimer}
      </p>
    </>
  );
};

export default InvestmentProjectionChart;
